import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HelpCircle, Check, Plus, EyeOff, Trash2, Brain, Loader2, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { InterviewPrepQuestion } from "./readInterviewPrep";

interface InterviewPrepQuestionsProps {
	questions: InterviewPrepQuestion[];
	notesByKey: Record<string, string>;
	onChangeNote: (questionKey: string, questionText: string, value: string) => void;
	onIgnore?: (key: string) => void;
	onDelete?: (key: string) => void;
	onLoadMore?: () => void;
	newQuestionsCount?: number;
	onReAnalyze?: () => void;
	isReAnalyzing?: boolean;
	onRegenerate?: () => void;
}

export const InterviewPrepQuestions = ({
	questions,
	notesByKey,
	onChangeNote,
	onIgnore,
	onDelete,
	onLoadMore,
	newQuestionsCount = 0,
	onReAnalyze,
	isReAnalyzing = false,
	onRegenerate,
}: InterviewPrepQuestionsProps) => {
	const { t } = useTranslation();
	const [openItem, setOpenItem] = useState<string | undefined>(
		questions.length > 0 ? questions[0].key : undefined
	);

	if (questions.length === 0) return null;

	return (
		<Card className="mb-6">
			<CardHeader className="pb-3">
				<CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
					<HelpCircle className="h-5 w-5 text-primary" />
					{t("matching.interviewPrep.questionsTitle", "Interview questions")}
					<Badge variant="secondary" className="ml-1 font-normal">
						{questions.length}
					</Badge>
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-0">
				<Accordion
					type="single"
					collapsible
					value={openItem}
					onValueChange={setOpenItem}
					className="w-full"
				>
					{questions.map((q) => {
						const note = notesByKey[q.key] || "";
						const answered = note.trim().length > 0;
						return (
							<AccordionItem
								key={q.key}
								value={q.key}
								className="border-b border-border last:border-b-0"
							>
								<AccordionTrigger className="hover:no-underline py-3">
									<div className="flex items-start gap-3 text-left flex-1 min-w-0 pr-2">
										<span className={`shrink-0 w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${
											answered
												? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
												: "bg-primary/10 text-primary"
										}`}>
											{answered ? <Check className="h-3.5 w-3.5" /> : q.index}
										</span>
										<span className="text-sm font-medium text-foreground leading-relaxed">
											{q.text}
										</span>
									</div>
								</AccordionTrigger>
								<AccordionContent className="pt-2 pb-4">
									<div className="pl-10 space-y-3">
										<Textarea
											placeholder={t(
												"matching.interviewPrep.notesPlaceholder",
												"Notes, expected answer, STAR breakdown, things to listen for…"
											)}
											value={note}
											onChange={(e) => onChangeNote(q.key, q.text, e.target.value)}
											className="min-h-[140px] resize-y"
											rows={5}
										/>
										<div className="flex justify-between items-center text-xs text-muted-foreground">
											<span>
												{note.length}{" "}
												{t("matching.interviewPrep.characters", "characters")}
											</span>
											<div className="flex items-center gap-2">
												{answered && (
													<Badge
														variant="outline"
														className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
													>
														<Check className="h-3 w-3 mr-1" />
														{t("matching.interviewPrep.prepared", "prepared")}
													</Badge>
												)}
												{onIgnore && (
													<Button
														variant="ghost"
														size="sm"
														onClick={() => onIgnore(q.key)}
														className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground gap-1"
													>
														<EyeOff className="h-3 w-3" />
														{t("matching.interviewPrep.ignore.button", "Ignore")}
													</Button>
												)}
												{onDelete && (
													<Button
														variant="ghost"
														size="sm"
														onClick={() => onDelete(q.key)}
														className="h-6 px-2 text-xs text-destructive/70 hover:text-destructive gap-1"
													>
														<Trash2 className="h-3 w-3" />
														{t("matching.interviewPrep.delete.button", "Delete")}
													</Button>
												)}
											</div>
										</div>
									</div>
								</AccordionContent>
							</AccordionItem>
						);
					})}
				</Accordion>

				{(onLoadMore || onReAnalyze || onRegenerate) && (
					<div className="pt-4 pb-1 flex flex-col items-center gap-3">
						{onLoadMore && (
							<Button
								variant="outline"
								size="sm"
								onClick={newQuestionsCount > 0 ? onLoadMore : undefined}
								disabled={newQuestionsCount === 0}
								className="gap-2 disabled:opacity-40"
							>
								<Plus className="h-4 w-4" />
								{t("matching.interviewPrep.loadMore.button", "Load more questions")}
								{newQuestionsCount > 0 && (
									<Badge variant="secondary" className="ml-1 font-normal text-xs">
										+{newQuestionsCount}
									</Badge>
								)}
							</Button>
						)}
						{onReAnalyze && (
							<Button
								variant="outline"
								size="sm"
								onClick={onReAnalyze}
								disabled={isReAnalyzing}
								className="gap-2 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
							>
								{isReAnalyzing ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Brain className="h-4 w-4" />
								)}
								{isReAnalyzing
									? t("matching.interviewPrep.reAnalyze.pending", "Queuing analysis…")
									: t("matching.interviewPrep.reAnalyze.button", "Regenerate analysis with answers")}
							</Button>
						)}
						{onRegenerate && (
							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										className="gap-2 text-muted-foreground hover:text-destructive hover:border-destructive/40"
									>
										<RotateCcw className="h-4 w-4" />
										{t("matching.interviewPrep.regenerate.button", "Regenerate")}
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>
											{t("matching.interviewPrep.regenerate.title", "Regenerate interview questions?")}
										</AlertDialogTitle>
										<AlertDialogDescription>
											{t(
												"matching.interviewPrep.regenerate.description",
												"This will replace the current question list with the one from the latest AI analysis and permanently delete every note you've written against these questions. This cannot be undone."
											)}
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>
											{t("matching.interviewPrep.regenerate.cancel", "Cancel")}
										</AlertDialogCancel>
										<AlertDialogAction
											onClick={onRegenerate}
											className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
										>
											{t("matching.interviewPrep.regenerate.confirm", "Regenerate and delete notes")}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
};
